import React, { useEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';

export interface ClassroomSelectorOption {
  title: string;
  value: string;
  active?: boolean;
}

export interface ClassroomSelectorProps {
  placeholder?: string;
  addNewItemTitle: string;
  selectedItem: string | null;
  options: Array<ClassroomSelectorOption>;
  addNewItemHandler: () => void;
  onChange: (id: ClassroomSelectorOption) => void;
}

const ClassroomSelector = ({
  placeholder = 'Choose',
  addNewItemTitle,
  options,
  addNewItemHandler,
  onChange,
  selectedItem,
}: ClassroomSelectorProps) => {
  const [selectedValue, setSelectedValue] = useState<ClassroomSelectorOption>({
    title: placeholder,
    value: '',
  });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedItem && options?.length) {
      const foundItem = options.find((item) => item.value === selectedItem);
      if (foundItem) {
        setSelectedValue(foundItem);
      }
    } else if (!selectedItem) {
      setSelectedValue({ title: placeholder, value: '' });
    }
  }, [selectedItem, options, placeholder]);

  return (
    <Menu as="div" className="relative font-sofiaSans">
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            className="bg-white bg-opacity-20 hover:bg-opacity-25 text-white font-bold text-lg leading-none py-2 px-3 sm:px-4 rounded-lg flex justify-between items-center min-w-[200px] lg:min-w-[275px] w-full"
          >
            <span className="pt-0.5 truncate">{selectedValue?.title}</span>
            <div>
              <svg className="ml-2 w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </Menu.Button>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 mt-2 w-full sm:w-[250px] bg-white rounded shadow-lg z-20 focus:outline-none font-sofiaSans">
              <div className="flex justify-between items-center px-4 py-2 border-b">
                <span className="text-base sm:text-lg font-medium text-black">{placeholder}</span>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                  onClick={() => buttonRef.current?.click()}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className={'flex flex-col max-h-screen md:max-h-[50vh] overflow-y-auto'}>
                {options &&
                  options.map((option, index) => (
                    <Menu.Item key={index}>
                      {({ active }) => (
                        <div
                          className={`px-4 py-2 border-b cursor-pointer text-black flex justify-start text-sm sm:text-lg font-medium hover:bg-gray-100 ${active ? 'bg-gray-100' : ''}`}
                          onClick={() => {
                            setSelectedValue(option);
                            onChange(option);
                            buttonRef.current?.click();
                          }}
                        >
                          <span className="truncate">{option.title}</span>
                        </div>
                      )}
                    </Menu.Item>
                  ))}
              </div>
              <div className="px-4 py-4">
                <button
                  className="w-full flex items-center justify-center text-white py-2 px-4 sm:px-6 rounded-full font-semibold text-sm sm:text-base bg-storyblueSecondLightText transition"
                  onClick={() => {
                    addNewItemHandler();
                    buttonRef.current?.click();
                  }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="truncate">{addNewItemTitle}</span>
                </button>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default ClassroomSelector;
